/**
 * EffectChipPopover — details popover for a chip in CombatRoundBar.
 *
 * Spec: `.kiro/specs/active-effects-round-tracker/design.md` § Component:
 * `EffectChipPopover`. Shows source, rounds remaining, Dismiss, and (when
 * the active-effect has damagePerRound) manual Roll + DM-damage controls.
 */

import React, { useState } from 'react';
import { useCharacterStore } from '../../stores/character';
import type { ActiveEffect } from '../../types/combatRound';

interface Props {
  label: string;
  source: string;
  rounds: number | null;
  effect?: ActiveEffect; // present when the chip is an active-effect
  combatStateId?: string; // present when the chip is a combat-state
  onClose: () => void;
}

const formatRounds = (r: number | null): string =>
  r === null ? 'indefinite' : `${r} round${r === 1 ? '' : 's'}`;

export function EffectChipPopover({
  label,
  source,
  rounds,
  effect,
  combatStateId,
  onClose
}: Props) {
  const removeActiveEffect = useCharacterStore((s) => s.removeActiveEffect);
  const deactivateState = useCharacterStore((s) => s.deactivateState);
  const takeDamage = useCharacterStore((s) => s.takeDamage);
  const addFatigue = useCharacterStore((s) => s.addFatigue);
  const [dmDamage, setDmDamage] = useState<string>('');

  const dismiss = () => {
    if (effect) removeActiveEffect(effect.id);
    else if (combatStateId) deactivateState(combatStateId);
    onClose();
  };

  const applyDamage = (amount: number) => {
    if (!effect?.damagePerRound) return;
    if (effect.damagePerRound.damageType === 'wounds') takeDamage(amount);
    else if (effect.damagePerRound.damageType === 'fatigue') addFatigue(amount);
    // 'characteristic' left to future work — not in v1 scope per minimal-code rule.
  };

  const handleRoll = () => {
    if (!effect?.damagePerRound) return;
    // Minimal built-in rolling for d10-based formulas. Per AC 6.4 nothing rolls
    // during tick; rolling only happens here on explicit click.
    const formula = effect.damagePerRound.formula.toLowerCase().replace(/\s+/g, '');
    const match = formula.match(/^(\d*)d(\d+)(?:\+(\d+))?$/);
    let rolled = 0;
    if (match) {
      const count = parseInt(match[1] || '1', 10);
      const faces = parseInt(match[2], 10);
      const bonus = match[3] ? parseInt(match[3], 10) : 0;
      for (let i = 0; i < count; i++) rolled += 1 + Math.floor(Math.random() * faces);
      rolled += bonus;
    } else {
      rolled = parseInt(formula, 10) || 0;
    }
    applyDamage(rolled);
  };

  const handleApplyDm = () => {
    const n = parseInt(dmDamage, 10);
    if (Number.isFinite(n) && n >= 0) applyDamage(n);
    setDmDamage('');
  };

  return (
    <div
      data-testid="effect-chip-popover"
      className="absolute z-50 mt-1 left-0 w-64 bg-surface-alt border border-line-strong rounded shadow-lg p-3 text-xs text-white"
      role="dialog"
      aria-label={`${label} details`}
    >
      <div className="font-semibold mb-1">{label}</div>
      <div className="text-ink-dim mb-1">{source}</div>
      <div className="text-ink-faint mb-2">Remaining: {formatRounds(rounds)}</div>

      {effect?.damagePerRound && (
        <div className="mb-2 border-t border-line-strong pt-2 space-y-1">
          <div className="text-ink">{effect.damagePerRound.description}</div>
          <div className="flex gap-1 items-center">
            <button
              type="button"
              onClick={handleRoll}
              data-testid="roll-damage-button"
              className="px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-500 text-white"
            >
              🎲 Roll
            </button>
            <input
              type="number"
              value={dmDamage}
              onChange={(e) => setDmDamage(e.target.value)}
              placeholder="DM damage"
              data-testid="dm-damage-input"
              className="w-20 px-1 py-0.5 rounded bg-page border border-line-strong text-white"
              min={0}
            />
            <button
              type="button"
              onClick={handleApplyDm}
              data-testid="apply-dm-damage-button"
              className="px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-0.5 rounded border border-line-strong text-ink hover:bg-surface-active"
        >
          Close
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="dismiss-effect-button"
          className="px-2 py-0.5 rounded bg-red-700 hover:bg-red-600 text-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
